import { useState, useEffect, useCallback } from 'preact/hooks';
import { googleAuthApi } from '@/api/googleAuth';
import { apiClient } from '@/api/client';
export function useGoogleDriveAuth(enabled = true) {
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    // Check connection status on mount
    const checkStatus = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await googleAuthApi.getStatus();
            if (response.data) {
                setStatus(response.data);
            }
            else {
                setStatus({ ProviderEmail: '', IsConnected: false });
            }
        }
        catch {
            setStatus({ ProviderEmail: '', IsConnected: false });
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        if (enabled)
            checkStatus();
    }, [enabled, checkStatus]);
    // Initiate OAuth flow in popup
    const connect = useCallback(async () => {
        setIsConnecting(true);
        try {
            const response = await googleAuthApi.initiate();
            if (!response.data?.authorizationUrl) {
                throw new Error('Failed to get authorization URL');
            }
            // Open popup
            const width = 600;
            const height = 800;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;
            const popup = window.open(response.data.authorizationUrl, 'google-oauth', `width=${width},height=${height},left=${left},top=${top},popup=yes`);
            // Handle popup blocked
            if (!popup) {
                console.error('OAuth popup was blocked by the browser');
                setIsConnecting(false);
                return;
            }
            // Listen for postMessage from callback page
            const apiOrigin = new URL(apiClient.getBaseUrl()).origin;
            const handleMessage = (event) => {
                if (event.origin !== window.location.origin && event.origin !== apiOrigin)
                    return;
                if (event.source !== popup)
                    return;
                if (event.data?.type === 'google-oauth-success') {
                    window.removeEventListener('message', handleMessage);
                    popup.close();
                    setIsConnecting(false);
                    checkStatus(); // Refresh status
                }
            };
            window.addEventListener('message', handleMessage);
            // Poll for popup close (user may close without completing)
            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer);
                    window.removeEventListener('message', handleMessage);
                    setIsConnecting(false);
                    checkStatus(); // Refresh — user may have completed OAuth
                }
            }, 500);
        }
        catch (error) {
            console.error('Failed to initiate Google OAuth:', error);
            setIsConnecting(false);
        }
    }, [checkStatus]);
    // Disconnect
    const disconnect = useCallback(async () => {
        try {
            await googleAuthApi.disconnect();
            setStatus({ ProviderEmail: '', IsConnected: false });
        }
        catch (error) {
            console.error('Failed to disconnect Google account:', error);
        }
    }, []);
    return {
        isConnected: status?.IsConnected ?? false,
        providerEmail: status?.ProviderEmail ?? '',
        isLoading,
        isConnecting,
        connect,
        disconnect,
        refreshStatus: checkStatus,
    };
}
